import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIssues } from '@/hooks/api-hooks';
import { Badge, Loader } from '@mantine/core';
import PageHeader from '@/components/page-header';
import { setWebTitle, formatPrice } from '@/lib/format-utils';
import { Link } from 'react-router-dom';
import {
    IconAlertTriangle,
    IconCopy,
    IconUserExclamation,
    IconExternalLink,
    IconCalendar,
    IconUser,
    IconBuildingStore,
    IconAirBalloon,
    IconMail,
    IconPhone,
    IconId,
} from '@tabler/icons-react';
import { PERSONS_PATH, BUSINESSES_PATH } from '@/lib/navigation';
import ServiceCardCatalog from '@/components/service-card-catalog';
import { SimpleReservationRow } from '@/components/simple-reservations-list';
import { Reservation, Service } from '@turizi/shared';

export default function IssuesPage(): JSX.Element {
    const { t } = useTranslation();
    const [activeCategory, setActiveCategory] = useState<string>('reservations');

    const { data: issuesRes, isLoading } = useIssues();

    setWebTitle(t('issues'));

    const reservationIssues = issuesRes?.data?.reservationIssues || [];
    // const duplicates = issuesRes?.data?.paxIssues?.duplicates || [];
    // const missingLastname = issuesRes?.data?.paxIssues?.missingLastname || [];
    const businessDuplicates = issuesRes?.data?.businessIssues?.duplicates || [];
    const paxBusinessConflicts = issuesRes?.data?.paxBusinessConflicts || [];
    const serviceIssues = issuesRes?.data?.serviceIssues || [];

    const businessIssuesCount = businessDuplicates.length;
    const paxBusinessConflictsCount = paxBusinessConflicts.length;

    const navItems = [
        {
            id: 'reservations',
            label: t('reservation_issues'),
            description: t('reservation_issues_desc'),
            icon: <IconCalendar size={18} />,
            count: reservationIssues.length,
            color: 'red',
        },
        {
            id: 'services',
            label: t('service_issues'),
            description: t('service_issues_desc'),
            icon: <IconAirBalloon size={18} />,
            count: serviceIssues.length,
            color: 'blue',
        },
        // {
        //     id: 'duplicates',
        //     label: t('duplicate_paxs'),
        //     description: t('duplicate_pax_desc'),
        //     icon: <IconCopy size={18} />,
        //     count: duplicates.length,
        //     color: 'amber',
        // },
        // {
        //     id: 'lastname',
        //     label: t('missing_lastname'),
        //     description: t('missing_lastname_desc'),
        //     icon: <IconUserExclamation size={18} />,
        //     count: missingLastname.length,
        //     color: 'orange',
        // },
        {
            id: 'businesses',
            label: t('business_issues'),
            description: t('duplicate_business_desc'),
            icon: <IconBuildingStore size={18} />,
            count: businessIssuesCount,
            color: 'purple',
        },
        {
            id: 'pax_business',
            label: t('pax_business_issues'),
            description: t('pax_business_conflict_desc'),
            icon: <IconAlertTriangle size={18} />,
            count: paxBusinessConflictsCount,
            color: 'rose',
        },
    ];

    const currentNav = navItems.find(n => n.id === activeCategory) || navItems[0];

    if (isLoading) {
        return (
            <div>
                <PageHeader title={t('issues')} />
                <div className="panel-content flex items-center justify-center p-12">
                    <Loader size="md" color='black' />
                </div>
            </div>
        );
    }

    return (
        <div>
            <PageHeader title={t('issues')} />
            <div className="panel-content">
                <div className="flex flex-col md:flex-row gap-5 items-start">
                    {/* Left Sidebar Menu */}
                    <div className="w-full md:w-70 shrink-0 bg-white p-2.5 rounded-2xl border border-slate-200/80 space-y-1.5 shadow-2xs">
                        {navItems.map((item) => {
                            const isActive = activeCategory === item.id;
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => setActiveCategory(item.id)}
                                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer text-left ${isActive
                                        ? 'bg-slate-900 text-white shadow-xs'
                                        : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
                                        }`}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <span className={isActive ? 'text-white' : 'text-slate-500'}>
                                            {item.icon}
                                        </span>
                                        <span className="whitespace-normal leading-tight">{item.label}</span>
                                    </div>
                                    {item.count > 0 && (
                                        <Badge
                                            size="xs"
                                            variant={isActive ? 'filled' : 'light'}
                                            color={isActive ? 'gray' : item.color}
                                            className="shrink-0 font-bold"
                                        >
                                            {item.count}
                                        </Badge>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 w-full space-y-4">
                        {/* Header bar for active category */}
                        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-2xs flex flex-wrap items-center justify-between gap-3">
                            <div className="space-y-0.5">
                                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                                    {currentNav.icon}
                                    {currentNav.label}
                                </h2>
                                <p className="text-xs text-slate-500 font-medium">
                                    {currentNav.description}
                                </p>
                            </div>
                            <Badge size="md" color={currentNav.color} variant="light" className="font-bold">
                                {currentNav.count} {t('total')}
                            </Badge>
                        </div>

                        {/* Reservation & Billing Issues Panel */}
                        {activeCategory === 'reservations' && (
                            <div className="space-y-4">
                                {reservationIssues.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-xs font-medium">
                                        {t('no_issues_found')}
                                    </div>
                                ) : (
                                    reservationIssues.map((issue: any, index: number) => {
                                        const hasAmount = issue.amount !== null && issue.amount !== undefined;
                                        const formattedDescription = issue.type === 'data_inconsistency'
                                            ? issue.description.split(', ').map((key: string) => t(key)).join(', ')
                                            : issue.description === 'no_provider_selected' || issue.type === 'missing_provider'
                                                ? t('no_provider_selected')
                                                : issue.description;

                                        return (
                                            <div
                                                key={`${issue.reservation_id || 'no-res'}-${issue.booking_id || index}`}
                                                className="bg-white border border-slate-200/90 rounded-2xl p-3 sm:p-4 shadow-2xs space-y-3 hover:border-slate-300 transition-all"
                                            >
                                                {/* Reservation Row if available */}
                                                {issue.reservation ? (
                                                    <SimpleReservationRow item={{ id: issue.reservation.id, reservation: issue.reservation }} />
                                                ) : (
                                                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {issue.reservation_id ? (
                                                                <Link
                                                                    to={`/reservations/${issue.reservation_id}`}
                                                                    target="_blank"
                                                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold text-xs rounded-lg transition-colors"
                                                                >
                                                                    <IconCalendar size={13} className="shrink-0" />
                                                                    Res #{issue.reservation_id}
                                                                    <IconExternalLink size={11} className="opacity-60 shrink-0" />
                                                                </Link>
                                                            ) : null}
                                                            {issue.booking_id && (
                                                                <Link
                                                                    to={`/service-bookings/${issue.booking_id}`}
                                                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold text-xs rounded-lg transition-colors"
                                                                >
                                                                    Booking #{issue.booking_id}
                                                                    <IconExternalLink size={11} className="opacity-60 shrink-0" />
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Explicit Error Description Alert Box */}
                                                <div className="flex items-start justify-between gap-3 bg-amber-50/90 border border-amber-200/90 rounded-xl p-3 text-xs text-amber-950 font-medium">
                                                    <div className="flex items-start gap-2.5">
                                                        <IconAlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                                                        <div className="flex-1 leading-relaxed">
                                                            <span className="font-bold block text-amber-900 mb-0.5">{t('reason')}:</span>
                                                            {formattedDescription}
                                                        </div>
                                                    </div>
                                                    {hasAmount && (
                                                        <div className="font-bold text-slate-900 text-xs px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200/60 rounded-lg shrink-0">
                                                            {formatPrice(issue.amount, { currency: issue.currency })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* Duplicate Passengers Panel */}
                        {/* {activeCategory === 'duplicates' && (
                            <div className="space-y-3">
                                {duplicates.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-xs font-medium">
                                        {t('no_duplicates_found')}
                                    </div>
                                ) : (
                                    duplicates.map((group: any) => (
                                        <div key={group.normalized} className="bg-white border border-slate-200/90 rounded-xl p-4 space-y-3 shadow-2xs">
                                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                                                <div className="flex items-center gap-2">
                                                    <IconUser className="text-amber-600" size={16} />
                                                    <span className="font-bold text-sm text-slate-900">{group.name}</span>
                                                </div>
                                                <Badge size="xs" color="amber" variant="light" className="font-bold">
                                                    {group.records.length} {t('records')}
                                                </Badge>
                                            </div>

                                            <div className="flex items-start gap-2 bg-amber-50/80 border border-amber-200/80 rounded-xl p-2.5 text-xs text-amber-900 font-medium">
                                                <IconAlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                                                <div className="flex-1 leading-relaxed">
                                                    <span className="font-bold block text-amber-950 mb-0.5">{t('reason')}:</span>
                                                    {t('duplicate_pax_desc')}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                                                {group.records.map((rec: any) => (
                                                    <div key={rec.id} className="bg-slate-50 border border-slate-200/60 rounded-lg p-3 text-xs space-y-1.5 hover:bg-slate-100/60 transition-colors">
                                                        <div className="flex items-center justify-between font-bold text-slate-900">
                                                            <span className="text-slate-700">Pax #{rec.id}</span>
                                                            <Link to={`${PAXS_PATH}?id=${rec.id}`} className="text-blue-600 hover:underline flex items-center gap-1 text-xxs font-semibold">
                                                                <span>ID #{rec.id}</span>
                                                                <IconExternalLink size={10} />
                                                            </Link>
                                                        </div>
                                                        {rec.email && (
                                                            <div className="flex items-center gap-1.5 text-slate-600">
                                                                <IconMail size={12} className="text-slate-400 shrink-0" />
                                                                <span>{rec.email}</span>
                                                            </div>
                                                        )}
                                                        {rec.phone && (
                                                            <div className="flex items-center gap-1.5 text-slate-600">
                                                                <IconPhone size={12} className="text-slate-400 shrink-0" />
                                                                <span>{rec.phone}</span>
                                                            </div>
                                                        )}
                                                        {rec.document && (
                                                            <div className="flex items-center gap-1.5 text-slate-600  text-xxs">
                                                                <IconId size={12} className="text-slate-400 shrink-0" />
                                                                <span>{rec.document}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )} */}

                        {/* Missing Last Name Panel */}
                        {/* {activeCategory === 'lastname' && (
                            <div className="space-y-3">
                                {missingLastname.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-xs font-medium">
                                        {t('no_missing_lastname_found')}
                                    </div>
                                ) : (
                                    missingLastname.map((rec: any) => (
                                        <div key={rec.id} className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs space-y-3 hover:border-slate-300 transition-all">
                                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                                                <div className="flex items-center gap-2">
                                                    <IconUserExclamation size={16} className="text-orange-500" />
                                                    <span className="font-bold text-sm text-slate-900">{rec.name}</span>
                                                </div>
                                                <Link to={`${PAXS_PATH}?id=${rec.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-semibold rounded-lg transition-colors">
                                                    Pax #{rec.id}
                                                    <IconExternalLink size={11} className="opacity-60" />
                                                </Link>
                                            </div>

                                            <div className="flex items-start gap-2 bg-orange-50/80 border border-orange-200/80 rounded-xl p-2.5 text-xs text-orange-950 font-medium">
                                                <IconAlertTriangle size={15} className="text-orange-600 shrink-0 mt-0.5" />
                                                <div className="flex-1 leading-relaxed">
                                                    <span className="font-bold block text-orange-900 mb-0.5">{t('reason')}:</span>
                                                    {t('missing_lastname_desc')}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600 pt-1">
                                                {rec.email && (
                                                    <div className="flex items-center gap-1.5">
                                                        <IconMail size={13} className="text-slate-400 shrink-0" />
                                                        <span>{rec.email}</span>
                                                    </div>
                                                )}
                                                {rec.phone && (
                                                    <div className="flex items-center gap-1.5">
                                                        <IconPhone size={13} className="text-slate-400 shrink-0" />
                                                        <span>{rec.phone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )} */}

                        {/* Duplicate Businesses Panel */}
                        {activeCategory === 'businesses' && (
                            <div className="space-y-3">
                                {businessDuplicates.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-xs font-medium">
                                        {t('no_duplicate_businesses_found')}
                                    </div>
                                ) : (
                                    businessDuplicates.map((group: any) => (
                                        <div key={group.normalized} className="bg-white border border-slate-200/90 rounded-xl p-4 space-y-3 shadow-2xs">
                                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                                                <div className="flex items-center gap-2">
                                                    <IconBuildingStore className="text-purple-600" size={16} />
                                                    <span className="font-bold text-sm text-slate-900">{group.name}</span>
                                                </div>
                                                <Badge size="xs" color="purple" variant="light" className="font-bold">
                                                    {group.records.length} {t('records')}
                                                </Badge>
                                            </div>

                                            <div className="flex items-start gap-2 bg-purple-50/80 border border-purple-200/80 rounded-xl p-2.5 text-xs text-purple-950 font-medium">
                                                <IconAlertTriangle size={15} className="text-purple-600 shrink-0 mt-0.5" />
                                                <div className="flex-1 leading-relaxed">
                                                    <span className="font-bold block text-purple-900 mb-0.5">{t('reason')}:</span>
                                                    {t('duplicate_business_desc')}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                                                {group.records.map((rec: any) => (
                                                    <div key={rec.id} className="bg-slate-50 border border-slate-200/60 rounded-lg p-3 text-xs space-y-1.5 hover:bg-slate-100/60 transition-colors">
                                                        <div className="flex items-center justify-between font-bold text-slate-900">
                                                            <span className="text-slate-700">{group.name}</span>
                                                            <Link to={`${BUSINESSES_PATH}/${rec.id}`} className="text-blue-600 hover:underline flex items-center gap-1 text-xxs font-semibold">
                                                                <span>ID #{rec.id}</span>
                                                                <IconExternalLink size={10} />
                                                            </Link>
                                                        </div>
                                                        {rec.email && (
                                                            <div className="flex items-center gap-1.5 text-slate-600">
                                                                <IconMail size={12} className="text-slate-400 shrink-0" />
                                                                <span>{rec.email}</span>
                                                            </div>
                                                        )}
                                                        {rec.phone && (
                                                            <div className="flex items-center gap-1.5 text-slate-600">
                                                                <IconPhone size={12} className="text-slate-400 shrink-0" />
                                                                <span>{rec.phone}</span>
                                                            </div>
                                                        )}
                                                        {rec.document && (
                                                            <div className="flex items-center gap-1.5 text-slate-600  text-xxs">
                                                                <IconId size={12} className="text-slate-400 shrink-0" />
                                                                <span>{rec.document}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Pax & Business Conflicts Panel */}
                        {activeCategory === 'pax_business' && (
                            <div className="space-y-3">
                                {paxBusinessConflicts.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-xs font-medium">
                                        {t('no_pax_business_issues_found')}
                                    </div>
                                ) : (
                                    paxBusinessConflicts.map((group: any) => (
                                        <div key={group.normalized} className="bg-white border border-slate-200/90 rounded-xl p-4 space-y-3 shadow-2xs">
                                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                                                <div className="flex items-center gap-2">
                                                    <IconAlertTriangle className="text-rose-500" size={16} />
                                                    <span className="font-bold text-sm text-slate-900">{group.name}</span>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <Badge size="xs" color="blue" variant="light" className="font-bold">
                                                        {group.persons.length} {t('paxs')}
                                                    </Badge>
                                                    <Badge size="xs" color="purple" variant="light" className="font-bold">
                                                        {group.businesses.length} {t('businesses')}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-2 bg-rose-50/80 border border-rose-200/80 rounded-xl p-2.5 text-xs text-rose-950 font-medium">
                                                <IconAlertTriangle size={15} className="text-rose-600 shrink-0 mt-0.5" />
                                                <div className="flex-1 leading-relaxed">
                                                    <span className="font-bold block text-rose-900 mb-0.5">{t('reason')}:</span>
                                                    {t('pax_business_conflict_desc')}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                                                {/* Persons Column */}
                                                <div className="space-y-2">
                                                    <div className="text-xxs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                                        <IconUser size={12} /> {t('paxs')}
                                                    </div>
                                                    {group.persons.map((rec: any) => (
                                                        <div key={rec.id} className="bg-blue-50/50 border border-blue-100 rounded-lg p-2.5 text-xs space-y-1">
                                                            <div className="flex items-center justify-between font-semibold">
                                                                <span className="text-blue-900">Pax #{rec.id}</span>
                                                                <Link to={`${PERSONS_PATH}?id=${rec.id}`} className="text-blue-600 hover:underline text-xxs">
                                                                    ID #{rec.id}
                                                                </Link>
                                                            </div>
                                                            {rec.email && <div className="text-slate-600">{rec.email}</div>}
                                                            {rec.phone && <div className="text-slate-600">{rec.phone}</div>}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Businesses Column */}
                                                <div className="space-y-2">
                                                    <div className="text-xxs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                                        <IconBuildingStore size={12} /> {t('businesses')}
                                                    </div>
                                                    {group.businesses.map((rec: any) => (
                                                        <div key={rec.id} className="bg-purple-50/50 border border-purple-100 rounded-lg p-2.5 text-xs space-y-1">
                                                            <div className="flex items-center justify-between font-semibold">
                                                                <span className="text-purple-900">Business #{rec.id}</span>
                                                                <Link to={`${BUSINESSES_PATH}/${rec.id}`} className="text-purple-600 hover:underline text-xxs">
                                                                    ID #{rec.id}
                                                                </Link>
                                                            </div>
                                                            {rec.email && <div className="text-slate-600">{rec.email}</div>}
                                                            {rec.phone && <div className="text-slate-600">{rec.phone}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Service Issues Panel using ServiceCardCatalog */}
                        {activeCategory === 'services' && (
                            <div className="space-y-4">
                                {serviceIssues.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-xs font-medium">
                                        {t('no_service_issues_found')}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {serviceIssues.map((service: Service) => (
                                            <div
                                                key={service.id}
                                                className="bg-white border border-slate-200/90 rounded-2xl p-4 space-y-3 shadow-2xs hover:border-slate-300 transition-all flex flex-col justify-between"
                                            >
                                                <ServiceCardCatalog
                                                    service={service}
                                                    showProviders={true}
                                                />
                                                <div className="flex items-start gap-2.5 bg-red-50/90 border border-red-200/90 rounded-xl p-3 text-xs text-red-950 font-medium">
                                                    <IconAlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                                                    <div className="flex-1 leading-relaxed">
                                                        <span className="font-bold block text-red-900 mb-0.5">{t('reason')}:</span>
                                                        {t('service_has_costs_no_providers')}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
